/**
 * Resolves the contextual framing of the inductee's /me page from the
 * programme it is anchored to plus today's date. Pure so it is trivial to
 * reason about and (later) unit test.
 *
 * Before the day -> "Onboarding". On the day -> the programme's day_label
 * ("Induction Day" / "Mobilisation Day"). After -> "Complete".
 */

export interface MeProgramme {
  id: string;
  name: string;
  kind: 'mobilisation' | 'onboarding';
  date: string; // YYYY-MM-DD
  status: 'planning' | 'active' | 'closed';
  day_label: string | null;
}

export type MeMode = 'onboarding' | 'day' | 'complete';

export interface MeContext {
  mode: MeMode;
  /** Short eyebrow label shown above the greeting. */
  eyebrow: string;
}

const DEFAULT_DAY_LABEL = 'Induction Day';

/**
 * @param programme the programme /api/me anchored to (null on a fresh install)
 * @param today YYYY-MM-DD in the viewer's locale; injectable for tests
 */
export function resolveMeContext(
  programme: MeProgramme | null,
  today: string,
): MeContext {
  // No programme, or the durable onboarding home: always onboarding framing.
  if (!programme || programme.kind === 'onboarding') {
    return { mode: 'onboarding', eyebrow: 'Onboarding' };
  }

  // A dated mobilisation. Closed -> read-only record.
  if (programme.status === 'closed') {
    return { mode: 'complete', eyebrow: 'Complete' };
  }

  const dayLabel = programme.day_label?.trim() || DEFAULT_DAY_LABEL;
  // On the day (status active, or the calendar date has arrived) -> day framing.
  if (programme.status === 'active' || today >= programme.date) {
    return { mode: 'day', eyebrow: dayLabel };
  }
  // Still in the run-up: onboarding/prep framing.
  return { mode: 'onboarding', eyebrow: 'Onboarding' };
}

/** Today as YYYY-MM-DD in local time. */
export function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}
